<template>
  <div id="search-bar-container" class="simple-search-bar-container" @keydown.enter="handleSearch" tabindex="0">

    <!-- Zone de recherche contenant le sélecteur de langue et le champ de saisie -->
    <div class="simple-bar-search-container">

      <!-- Corps principal : sélecteur de langue + champ de recherche en ligne -->
      <div class="search-container-body">

        <div id="search-select-filter">
          <!-- Wrapper pour le sélecteur de langue personnalisé -->
          <div id="language-select-wrapper" class="language-select-wrapper">
            <div class="form-group">
              <!-- Select personnalisé pour supporter les icônes/images de drapeaux -->
              <!-- Affichage conditionnel basé sur l'état ouvert/fermé -->
              <div class="custom-select-wrapper" :class="{ 'open': isSelectOpen }" @click="toggleSelect">
                <!-- Affichage de la langue actuellement sélectionnée -->
                <div class="custom-select-display">
                  <span v-if="selectedLanguageObject">
                    <!-- Affichage conditionnel : image de drapeau ou emoji selon le type d'icône -->
                    <img v-if="isImagePath(selectedLanguageObject.icon)" :src="selectedLanguageObject.icon"
                      :alt="selectedLanguageObject.name" class="flag-image" />
                    <span v-else class="emoji-icon">{{ selectedLanguageObject.icon }}</span>
                    <!-- Libellé de la langue sélectionnée -->
                    <span class="language-label">{{ selectedLanguageObject.label }}</span>
                  </span>

                  <svg class="svg-icon svg-angle-down" aria-hidden="true" focusable="false">
                    <use xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#angle-down"></use>
                  </svg>
                </div>
                <!-- Menu déroulant des langues disponibles (affiché conditionnellement) -->
                <div class="custom-select-dropdown" v-show="isSelectOpen">
                  <!-- Liste dynamique des langues avec gestion de la sélection -->
                  <div v-for="language in availableLanguages" :key="language.code" class="custom-select-option"
                    :class="{ 'selected': language.code === selectedLang }" @click.stop="selectLanguage(language)">
                    <!-- Même logique d'affichage que pour la langue sélectionnée -->
                    <img v-if="isImagePath(language.icon)" :src="language.icon" :alt="language.name"
                      class="flag-image" />
                    <span v-else class="emoji-icon">{{ language.icon }}</span>
                    <span class="language-label">{{ language.label }}</span>
                  </div>
                </div>
              </div>

              <!-- Select HTML natif caché : maintient la compatibilité avec les formulaires -->
              <!-- et les lecteurs d'écran pour l'accessibilité -->
              <select class="sr-only" name="languageSelect" id="languageSelect" :value="selectedLang"
                @change="handleLanguageChange">
                <option v-for="language in availableLanguages" :key="language.code" :value="language.code">
                  {{ language.label }}
                </option>
              </select>
            </div>

          </div>

          <!-- Wrapper pour le sélecteur de catégorie -->
          <div class="category-select-wrapper">
            <div class="form-group">
              <select class="form-control input-sm category-select" name="typesSelect" id="typesSelect"
                :value="selectedTypesComputed" @change="handleTypesChange">
                <option v-for="type in availableTypes" :key="type.code" :value="type.code">
                  {{ type.label }}
                </option>
              </select>
            </div>
          </div>
        </div>


        <!-- Groupe de champ de recherche avec bouton intégré -->
        <div id="pattern-recherche-container" class="input-group input-group-sm search-group">
          <!-- Label invisible mais accessible aux lecteurs d'écran -->
          <label for="patternRecherche" class="sr-only">Recherche</label>
          <!-- Champ de saisie principal pour la recherche -->
          <input class="form-control input-sm" name="patternRecherche" type="search" id="patternRecherche"
            :value="localSearchQuery" @input="updateSearchQuery" placeholder="Rechercher terminologies,concepts,..." />
          <!-- Bouton de soumission de la recherche -->
          <div class="input-group-btn">
            <button id="pattern-recherche-btn" type="button" class="btn btn-sm btn--plain btn--primary btn--icon-before"
              @click="handleSearch">Rechercher</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Conteneur des options de filtrage de recherche -->
    <div class="search-option-container">
      <search-history @replay-search="handleReplaySearch" @history-cleared="handleHistoryCleared" />

      <div id="search-avec-operateur" class="custom-control custom-checkbox">
        <input type="checkbox" class="custom-control-input" name="recherche-operateur-cb" id="recherche-operateur-cb"
          :checked="extendedSearch" @change="updateExtendedSearch">
        <label id="recherche-operateur-cb-label" class="custom-control-label" for="recherche-operateur-cb">Recherche
          en mode étendu</label>
        <button type="button" data-toggle="tooltip" data-placement="top" data-html="true" data-trigger="hover focus"
          data-delay='{"show":0,"hide":800}' data-container="body"
          title="Caractères à utiliser : + - = && || > < ! ( ) { } [ ] ^ &quot; ~ * ? : \ /  AND OR NOT<br/><a href='/guide-dutilisation-de-la-recherche-avancee/' style='color: white !important; text-decoration: underline !important;' target='_blank'>Plus d'informations ici</a>">
          <svg class="svg-icon svg-question-circle" aria-hidden="true" focusable="false">
            <use xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#question-circle"></use>
          </svg>
          <span class="sr-only">Aide</span>
        </button>
      </div>
    </div>
  </div>

</template>

<script>
// Importation des utilitaires de configuration des langues et types
import { defaultLanguages, getDefaultLanguage, isAllLanguagesCode } from '../enums/languages.js';
import { defaultTypes, isAllTypesCode, getAvailableTypeCodes } from '../enums/types.js';
import SearchHistory from './SearchHistory.vue';
import { saveSearch } from '../../../common/historyUtils.js';


export default {
  name: "SimpleSearchBar",
  components: {
    SearchHistory,
  },

  // Propriétés reçues du composant parent
  props: {
    // Langue actuellement sélectionnée (chaîne de caractères)
    selectedLanguage: {
      type: String,
      default: 'fr'
    },
    // Liste des langues disponibles dans le sélecteur
    // Utilise une fonction de validation pour s'assurer de la structure correcte
    languages: {
      type: Array,
      default: () => defaultLanguages,
    },
    // Valeur du champ de recherche principal
    searchQuery: {
      type: String,
      default: ''
    },
    // Option "Recherche étendue"
    extendedSearch: {
      type: Boolean,
      default: false
    },
    // Type sélectionné
    selectedType: {
      type: String,
      default: 'all'
    },
    // Liste des types disponibles dans le sélecteur
    types: {
      type: Array,
      default: () => defaultTypes,
    }
  },

  // Données réactives locales du composant
  data() {
    return {
      // Langue sélectionnée localement (priorité sur les props)
      currentLanguage: null,
      // État d'ouverture du menu déroulant personnalisé
      isSelectOpen: false,
      // Types sélectionnés localement (priorité sur les props)
      currentType: null,
      // Valeur locale de la requête de recherche
      localSearchQuery: '',
    };
  },

  // Propriétés calculées pour la logique de sélection des langues
  computed: {
    // Langues disponibles : utilise les props ou fallback vers la config par défaut
    availableLanguages() {
      return this.languages && this.languages.length > 0 ? this.languages : defaultLanguages;
    },

    // Types disponibles : utilise les props ou fallback vers la config par défaut
    availableTypes() {
      return this.types && this.types.length > 0 ? this.types : defaultTypes;
    },

    // Getter/Setter pour la langue sélectionnée avec logique de priorité
    selectedLang: {
      get() {
        // Priorité : langue locale > prop > langue par défaut
        if (this.currentLanguage) {
          return this.currentLanguage;
        }

        // Gère le cas où selectedLanguage est un string
        if (typeof this.selectedLanguage === 'string') {
          return this.selectedLanguage;
        }

        return this.getInitialLanguage();
      },
      set(value) {
        // Met à jour la langue locale et émet l'événement vers le parent
        this.currentLanguage = value;
        this.$emit('languages-changed', value);
      }
    },

    // Objet complet de la langue sélectionnée (avec icône, label, etc.)
    selectedLanguageObject() {
      return this.availableLanguages.find(lang => lang.code === this.selectedLang);
    },

    // Getter/Setter pour les types sélectionnés avec logique de priorité
    selectedTypesComputed: {
      get() {
        // Priorité : types locaux > prop > types par défaut
        return this.currentType || this.selectedType || this.getInitialTypes();
      },
      set(value) {
        // Met à jour les types locaux et émet l'événement vers le parent
        this.currentType = value;
        this.$emit('update:selectedTypes', value);
        this.$emit('types-changed', value);
      }
    }
  },

  // Méthodes du composant
  methods: {

    /**
     * Gère la relance d'une recherche depuis l'historique
     * @param {Object} searchData - Les données de recherche à relancer
     */
    handleReplaySearch(searchData) {
      // Pas de langue enregistrée dans l'historique => on considère "toutes les langues"
      const languageToReplay = searchData.language || 'all';

      // Met à jour le sélecteur de langue affiché
      this.selectedLang = languageToReplay;

      // Met à jour les propriétés de recherche avec les données de l'historique
      this.$emit('update:searchQuery', searchData.query);
      this.$emit('update:extendedSearch', searchData.options.extendedSearch);

      if (searchData.language === undefined || searchData.language === null) {
        searchData.language = '';
      }

      // Lance la recherche avec les données de l'historique
      this.$emit('search-submitted', searchData);
    },

    /**
     * Gère la suppression de tout l'historique
     */
    handleHistoryCleared() {
      // Émet un événement pour informer le composant parent
      this.$emit('history-cleared');
    },

    /**
     * Détermine la langue à sélectionner par défaut au chargement
     * @returns {string} Code de la langue par défaut
     */
    getInitialLanguage() {
      const defaultLang = getDefaultLanguage(this.availableLanguages);
      return defaultLang ? defaultLang.code : 'fr';
    },

    /**
     * Détermine les types à sélectionner par défaut au chargement
     * @returns {string} Code des types par défaut
     */
    getInitialTypes() {
      return 'all'; // Types par défaut : "Tous les types"
    },






    /**
     * Gestionnaire pour le changement de langue via le select HTML natif (accessibilité)
     * @param {Event} event - Événement de changement du select
     */
    handleLanguageChange(event) {
      const selectedCode = event.target.value;
      this.selectedLang = selectedCode;

      // Émettre un événement spécifique si "toutes les langues" est sélectionné
      if (isAllLanguagesCode(selectedCode)) {
        this.$emit('all-languages-selected', selectedCode);
      }

      // Trouver l'objet langue complet pour des informations supplémentaires
      const selectedLanguage = this.availableLanguages.find(lang => lang.code === selectedCode);
      if (selectedLanguage) {
        this.$emit('language-object-changed', selectedLanguage);
      }
    },



    /**
     * Détermine si une valeur d'icône est un chemin vers une image
     * @param {string} iconValue - Valeur de l'icône à tester
     * @returns {boolean} Vrai si c'est un chemin d'image
     */
    isImagePath(iconValue) {
      // Vérifie si l'icône est un chemin d'image (extensions courantes)
      return iconValue && typeof iconValue === 'string' &&
        (iconValue.includes('.svg') || iconValue.includes('.png') ||
          iconValue.includes('.jpg') || iconValue.startsWith('/'));
    },

    /**
     * Bascule l'état d'ouverture du menu déroulant personnalisé
     */
    toggleSelect() {
      this.isSelectOpen = !this.isSelectOpen;
    },

    /**
     * Sélectionne une langue via le menu déroulant personnalisé
     * @param {Object} language - Objet langue sélectionné
     */
    selectLanguage(language) {
      this.selectedLang = language.code;
      this.isSelectOpen = false;

      // Émettre les mêmes événements que handleLanguageChange pour la cohérence
      if (isAllLanguagesCode(language.code)) {
        this.$emit('all-languages-selected', language.code);
      }
      this.$emit('language-object-changed', language);
    },

    /**
     * Ferme le menu déroulant si un clic se produit en dehors du composant
     * @param {Event} event - Événement de clic global
     */
    closeSelectOnOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.isSelectOpen = false;
      }
    },

    /**
     * Met à jour la valeur du champ de recherche
     * @param {Event} event - Événement de saisie du champ de recherche
     */
    updateSearchQuery(event) {
      this.localSearchQuery = event.target.value;
      this.$emit('update:searchQuery', event.target.value);
    },

    /**
     * Met à jour l'option "Recherche étendue"
     * @param {Event} event - Événement de changement de la case à cocher
     */
    updateExtendedSearch(event) {
      this.$emit('update:extendedSearch', event.target.checked);
    },

    /**
     * Gestionnaire pour le changement de types
     * @param {Event} event - Événement de changement du sélecteur
     */
    handleTypesChange(event) {
      this.selectedTypesComputed = event.target.value;
    },

    /**
     * Gestionnaire pour le clic sur le bouton "Rechercher"
     * Utilise les propriétés pour créer l'objet de données de recherche
     */
    handleSearch() {
      // Détermine les langues à envoyer selon la sélection
      let languageToSend;
      if (this.selectedLang === 'all') {
        languageToSend = "";
      } else {
        // Sinon, envoie seulement la langue sélectionnée
        languageToSend = this.selectedLang;
      }

      // Détermine les types à envoyer selon la sélection
      let typesToSend;
      if (this.selectedTypesComputed === 'all' || isAllTypesCode(this.selectedTypesComputed)) {
        // Si "tous les types" est sélectionné, envoie tous les types disponibles
        typesToSend = getAvailableTypeCodes(this.availableTypes);
      } else {
        // Sinon, envoie seulement le type sélectionné
        typesToSend = [this.selectedTypesComputed];
      }

      // Crée l'objet de données de recherche à partir des props
      const searchData = {
        query: this.localSearchQuery.trim(),
        language: languageToSend,
        types: typesToSend,
        options: {
          extendedSearch: this.extendedSearch
        }
      };

      // Sauvegarder la recherche dans l'historique
      this.saveSearchToHistory(searchData);

      // Émet l'événement vers le composant parent avec les données de recherche
      this.$emit('search-submitted', searchData);
    },

    /**
     * Sauvegarde la recherche dans l'historique
     * @param {Object} searchData - Les données de recherche à sauvegarder
     */
    saveSearchToHistory(searchData) {
      try {
        // Préparer les données pour l'historique
        const historyItem = {
          query: searchData.query,
          advanced: searchData.options.extendedSearch,
          languages: searchData.language ? [searchData.language] : [],
          types: searchData.types,
          conceptsCount: undefined, // Sera mis à jour quand on aura les résultats
          terminologiesCount: undefined // Sera mis à jour quand on aura les résultats
        };

        // Sauvegarder dans l'historique
        const saved = saveSearch(historyItem);

        if (!saved) {
          console.warn('Échec de la sauvegarde de la recherche dans l\'historique');
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la recherche:', error);
      }
    },
  },

  // Crochet de cycle de vie : après montage du composant dans le DOM
  mounted() {
    // Initialise la langue courante avec la propriété ou la langue par défaut
    let initialLanguage = this.getInitialLanguage();
    // Gère le cas où selectedLanguage est une chaîne de caractères
    initialLanguage = this.selectedLanguage;

    this.currentLanguage = initialLanguage;

    // Initialise les types courants avec la propriété ou les types par défaut
    this.currentType = this.selectedType || this.getInitialTypes();

    // Initialise la requête de recherche locale avec la prop
    this.localSearchQuery = this.searchQuery;

    // Ajoute un écouteur global pour fermer le menu déroulant sur clic extérieur
    document.addEventListener('click', this.closeSelectOnOutsideClick);
  },

  // Crochet de cycle de vie : avant destruction du composant
  beforeDestroy() {
    // Nettoie l'écouteur d'événement pour éviter les fuites mémoire
    document.removeEventListener('click', this.closeSelectOnOutsideClick);
  },

  // Observateurs pour réagir aux changements de propriétés
  watch: {
    // Surveille les changements de la propriété selectedLanguage du parent
    selectedLanguage(newVal) {
      let newLanguage = null;

      // Gère le cas où selectedLanguage est un tableau (prend le premier élément)
      if (Array.isArray(newVal) && newVal.length > 0) {
        newLanguage = newVal[0];
      }
      // Gère le cas où selectedLanguage est une chaîne de caractères
      else if (typeof newVal === 'string') {
        newLanguage = newVal;
      }

      if (newLanguage && newLanguage !== this.currentLanguage) {
        this.currentLanguage = newLanguage;
      }
    },

    // Surveille les changements de la propriété selectedTypes du parent
    selectedTypes(newVal) {
      if (newVal !== this.currentTypes) {
        this.currentTypes = newVal;
      }
    },

    // Surveille les changements de la propriété searchQuery du parent
    searchQuery(newVal) {
      if (newVal !== this.localSearchQuery) {
        this.localSearchQuery = newVal;
      }
    }
  }
};
</script>


<style scoped>
/* Styles pour le composant SimpleSearchBar */

/* Corps du conteneur de recherche : disposition flex horizontale */
.search-container-body {
  display: flex;
  align-items: flex-start;
  /* Alignement en haut pour cohérence visuelle */
  width: 100%;
}

#search-select-filter {
  display: flex;
  align-items: flex-start;
  flex-direction: row;
}


/* Empêche le retour à la ligne du label de l'option "avec opérateur" */
#recherche-operateur-cb-label {
  white-space: nowrap;
  align-self: flex-start;
}

/* Conteneur principal de recherche : layout horizontal par défaut */
.simple-bar-search-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 90%;
}

.search-option-container {
  display: flex;
  flex-direction: row-reverse;
  width: 90%;
}

/* Positionnement de la checkbox "avec opérateur" */
#search-avec-operateur {
  align-self: flex-end;
  display: flex;
  align-items: flex-end;
  flex-grow: 1;
}

/* Language select styling */
.language-select-wrapper {
  flex-shrink: 0;
}

/* Category select styling */
.category-select-wrapper {
  flex-shrink: 0;
  margin-left: 0;
}

.category-select {
  width: auto;
  min-width: 180px;
  max-width: 200px;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  padding: 4px 8px;
  border-radius: 0;
  height: 43px;
}

.language-select {
  width: 60px;
  min-width: 60px;
}

/* Styling pour le sélecteur avec icônes */
.language-select-with-icons {
  width: auto;
  min-width: 90px;
  max-width: 130px;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  padding: 4px 8px;
}

.language-select-with-icons option {
  font-size: 14px;
  padding: 4px 8px;
  line-height: 1.4;
}

/* Support pour les émojis/drapeaux dans les options */
.language-select-with-icons option:before {
  font-style: normal;
}

/* Styles pour le select personnalisé */
.custom-select-wrapper {
  position: relative;
  display: inline-block;
  min-width: 100px;
  max-width: 130px;
  height: 43px;
}

.custom-select-display {
  background: white;
  border: 1px solid #ced4da;
  border-radius: 2.1rem 0 0 2.1rem;
  padding: 4px 20px 4px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  min-height: 30px;
  position: relative;
  height: 43px;
}

.custom-select-wrapper:focus-within .custom-select-display,
.custom-select-wrapper.open .custom-select-display {
  border-color: var(--primary);
  outline: 0;
}

.custom-select-display .svg-angle-down {
  margin-left: auto;
  width: 0.7rem;
  height: 0.7rem;
}


.custom-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #343852;
  border-top: none;
  border-radius: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
}

.custom-select-option {
  padding: 6px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  transition: background-color 0.2s;
}

.custom-select-option:hover {
  background-color: var(--primary);
  color: white;
}


.flag-image {
  width: 20px;
  height: 25px;
  border-radius: 2px;
  margin-right: 10px;
}

.emoji-icon {
  width: 20px;
  text-align: center;
  font-size: 16px;
}

.language-label {
  flex: 1;
}

#patternRecherche {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: none;
}

#pattern-recherche-container {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  height: 43px;
}

#pattern-recherche-btn {
  height: 43px;
}

.form-group {
  margin-bottom: 0 !important;
}

/* Centrage du SVG dans le bouton d'aide */
#search-avec-operateur button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border: none;
  background: transparent;
}

.search-group {
  width: 650px;
  position: relative;
}


/* Bureau et tablette : ajustements de largeur maximale */
@media screen and (max-width: 1300px) {
  .search-option-container {
    width: 90% !important;
  }

  .simple-bar-search-container {
    max-width: 100% !important;
    flex-direction: column;
    justify-content: flex-start;
  }

  .search-group {
    width: 550px !important;
  }
}



/* Bureau et tablette : ajustements de largeur maximale */
@media screen and (min-width: 768px) and (max-width: 1200px) {

  .search-group {
    width: 440px !important;
  }

  .simple-bar-search-container {
    width: 100% !important;
    flex-direction: column;
    justify-content: flex-start;
  }

  .category-select-wrapper {
    margin-left: 0;
  }

}


/* Bureau et tablette : ajustements de largeur maximale */
@media screen and (min-width: 640px) and (max-width: 768px) {
  .search-group {
    width: 100% !important;
  }
}

/* Bureau et tablette : ajustements de largeur maximale */
@media screen and (max-width: 640px) {

  .search-group {
    width: 100% !important;
  }

  .search-option-container {
    width: 100% !important;
    flex-direction: column-reverse !important;
  }

  /* Positionnement de la checkbox "avec opérateur" */
  #search-history-btn {
    align-self: center !important;
  }

  #search-avec-operateur {
    align-self: flex-start !important;
    margin-bottom: 10px;
  }

  #patternRecherche {
    border-radius: 2.1rem 0 0 2.1rem !important;
  }

  #pattern-recherche-container {
    margin-top: 10px;
    border-radius: 2.1rem !important;
  }


  #typesSelect {
    border: 1px solid #ced4da !important;
    border-radius: 0 2.1rem 2.1rem 0 !important;
  }

  .search-container-body {
    flex-direction: column !important;
  }

  .simple-bar-search-container {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    width: 100% !important;
  }

  #search-select-filter {
    width: 100% !important;
    display: flex !important;
    justify-content: center !important;
  }

  .simple-search-bar-container {
    width: 100% !important;
  }

  .category-select-wrapper {
    margin-left: 0 !important;
  }

  .category-select {
    width: 100% !important;
  }
}





/* Styles pour le modal plein écran */
#modal1.modal {
  padding: 0 !important;
  z-index: 1050 !important;
}

#modal1 .modal-dialog {
  max-width: none !important;
  width: 100vw !important;
  height: 100vh !important;
  margin: 0 !important;
  z-index: 1050 !important;
}

#modal1 .modal-content {
  height: 100vh !important;
  border-radius: 0 !important;
  border: none !important;
  z-index: 1050 !important;
}

#modal1 .modal-body {
  flex: 1 !important;
  overflow-y: auto !important;
}

/* Ensure modal backdrop is properly layered */
.modal-backdrop {
  z-index: 1040 !important;
}
</style>

<style>
/* Global styles for tooltip links (non-scoped to affect Bootstrap tooltip) */
.tooltip a,
.tooltip-inner a,
.tooltip.show a,
.tooltip.bs-tooltip-top a,
.tooltip.bs-tooltip-bottom a,
.tooltip.bs-tooltip-left a,
.tooltip.bs-tooltip-right a,
.tooltip .tooltip-inner a {
  color: #ffffff !important;
  text-decoration: underline !important;
}

.tooltip a:hover,
.tooltip-inner a:hover,
.tooltip.show a:hover,
.tooltip .tooltip-inner a:hover {
  color: #f0f0f0 !important;
  text-decoration: underline !important;
}

.tooltip a:visited,
.tooltip-inner a:visited {
  color: #ffffff !important;
}
</style>
